import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { applyActiveTabPin, computeOverflowSplit, OverflowSplit } from "./overflow-split";

// Matches NavbarSection's `gap-3` (0.75rem) and the "More ▾ · N" trigger's
// natural footprint.
const GAP_PX = 12;
const TRIGGER_WIDTH_PX = 96;

export interface OverflowNavItems<T> {
    containerRef: React.RefObject<HTMLDivElement>;
    measureRef: React.RefObject<HTMLDivElement>;
    visible: T[];
    overflow: T[];
}

/**
 * Splits `items` into what fits on one row of `containerRef` and what
 * overflows, re-measuring whenever the container resizes (including sidebar
 * collapse/expand, which doesn't fire a window resize event). `activeIndex`
 * is always kept visible even if it would otherwise overflow — by swapping
 * it in for the last naturally-visible item, not by expanding the visible
 * count (which would defeat collapsing entirely whenever the active item is
 * late in the list).
 *
 * Widths are read from a same-styled but visually hidden row attached to
 * `measureRef` — the real (possibly collapsed) row can't be used for this,
 * since items removed from it can no longer be measured on the next resize.
 */
export function useOverflowNavItems<T>(
    items: T[],
    activeIndex: number,
): OverflowNavItems<T> {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [split, setSplit] = useState<OverflowSplit<T>>({
        visible: items,
        overflow: [],
    });

    const recompute = useCallback(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) {
            return;
        }
        const widths = Array.from(measure.children).map(
            child => (child as HTMLElement).offsetWidth,
        );
        const naturalSplit = computeOverflowSplit(
            widths,
            container.clientWidth,
            TRIGGER_WIDTH_PX,
            GAP_PX,
        );
        setSplit(applyActiveTabPin(items, naturalSplit, activeIndex));
    }, [activeIndex, items]);

    useLayoutEffect(() => {
        recompute();
        const container = containerRef.current;
        if (!container) {
            return undefined;
        }
        const observer = new ResizeObserver(() => recompute());
        observer.observe(container);
        return () => observer.disconnect();
    }, [recompute, items]);

    return {
        containerRef,
        measureRef,
        visible: split.visible,
        overflow: split.overflow,
    };
}
